import React, { useState, useEffect } from "react";
import {
    Box,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Tooltip as MuiTooltip,
    IconButton,
} from "@mui/material";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { getSensorDataById } from "../../api"; // API Call
import { NavigateBefore, NavigateNext } from '@mui/icons-material';

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        const { captureTime, value, originalImage, processedImage } = payload[0].payload;
        return (
            <Paper sx={{ p: 2, textAlign: "center", width: 250 }}>
                <Typography variant="body2" gutterBottom>
                    Time: {captureTime}
                </Typography>
                <Typography variant="body2" gutterBottom>
                    Count: {value}
                </Typography>
                <Box display="flex" justifyContent="space-between" mt={1}>
                    <img src={originalImage} alt="Original" style={{ width: "45%", borderRadius: 8 }} />
                    <img src={processedImage} alt="Processed" style={{ width: "45%", borderRadius: 8 }} />
                </Box>
            </Paper>
        );
    }
    return null;
};

const ValueImageChart = ({ sensorId, label, dataKey }) => {
    const [sensorData, setSensorData] = useState([]);
    const [tabIndex, setTabIndex] = useState(0);
    const [selectedData, setSelectedData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [limit] = useState(20); // Default to 20 records per page
    const [hasNextPage, setHasNextPage] = useState(false);

    // Fetch sensor data from API
    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await getSensorDataById(sensorId, { page, limit });
                setSensorData(response.data.data || []);
                setHasNextPage(response.data.hasNextPage);
            } catch (err) {
                setError("Failed to load sensor data.");
                console.error("Error fetching sensor data:", err);
            } finally {
                setLoading(false);
            }
        };

        if (sensorId) fetchData();
    }, [sensorId, page, limit]);

  

    if (loading) return <CircularProgress />;
    if (error) return <Alert severity="error">{error}</Alert>;
    if (!sensorData || sensorData.length === 0) return <Alert severity="info">No sensor data available.</Alert>;

    // Format data for chart
    const chartData = sensorData.map((d) => ({
        captureTime: new Date(d.captureTime).toLocaleTimeString(),
        value: d.data[dataKey],
        originalImage: d.data.originalImage,
        processedImage: d.data.annotatedImage,
    })).reverse(); // Reverse the array to show data in chronological order in the chart

    // Update the PaginationControls component
    const PaginationControls = () => (
        <Box display="flex" justifyContent="center" alignItems="center" mt={2}>
            <IconButton 
                size="small"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
            >
                <NavigateBefore />
            </IconButton>
            <Typography sx={{ mx: 2, minWidth: '24px', textAlign: 'center' }}>
                {page}
            </Typography>
            <IconButton 
                size="small"
                disabled={!hasNextPage}
                onClick={() => setPage(p => p + 1)}
            >
                <NavigateNext />
            </IconButton>
        </Box>
    );

    return (
        <Box>
            <Typography variant="h6" align="center" gutterBottom>
                {label} Over Time
            </Typography>

            {/* Tabs for Chart & Table */}
            <Tabs value={tabIndex} onChange={(event, newIndex) => setTabIndex(newIndex)}>
                <Tab label="Chart" />
                <Tab label="List" />
            </Tabs>

            {/* Chart View */}
            {tabIndex === 0 && (
                <Box p={2}>
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 25 }}>
                            <XAxis 
                                dataKey="captureTime" 
                                tick={{ fontSize: 12 }} 
                                angle={-45} 
                                textAnchor="end"
                                height={60}
                                interval={0}
                            />
                            <YAxis label={{ value: label, angle: -90, position: "insideLeft" }} />
                            <Tooltip content={<CustomTooltip />} />
                            <Line
                                type="monotone"
                                dataKey="value"
                                stroke="#1976d2"
                                strokeWidth={2}
                                dot={{ r: 6 }}
                                activeDot={{ onClick: (e) => setSelectedData(e.payload) }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                    <PaginationControls />
                </Box>
            )}

            {/* Table View */}
            {tabIndex === 1 && (
                <Box p={2}>
                    <TableContainer component={Paper} sx={{ maxHeight: 400, overflowY: "auto" }}>
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Capture Time</TableCell>
                                    <TableCell>{label}</TableCell>
                                    <TableCell>Original</TableCell>
                                    <TableCell>Processed</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sensorData.map((row, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{new Date(row.captureTime).toLocaleTimeString()}</TableCell>
                                        <TableCell>{row.data[dataKey]}</TableCell>
                                        <TableCell>
                                            <MuiTooltip title={<img src={row.data.originalImage} alt="Original" width="200" />}>
                                                <img src={row.data.originalImage} alt="Thumbnail" style={{ width: 50, borderRadius: 5 }} />
                                            </MuiTooltip>
                                        </TableCell>
                                        <TableCell>
                                            <MuiTooltip title={<img src={row.data.annotatedImage} alt="Processed" width="200" />}>
                                                <img src={row.data.annotatedImage} alt="Thumbnail" style={{ width: 50, borderRadius: 5 }} />
                                            </MuiTooltip>
                                        </TableCell>
                                        <TableCell>
                                            <Button variant="outlined" onClick={() => setSelectedData(row)}>
                                                View Images
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <PaginationControls />
                </Box>
            )}

            {/* Image Popup */}
            <Dialog open={!!selectedData} onClose={() => setSelectedData(null)}>
                <DialogTitle>{label} Detection Images</DialogTitle>
                <DialogContent>
                    {selectedData && (
                        <Box display="flex" justifyContent="space-between" p={2}>
                            <Box textAlign="center">
                                <Typography variant="body1">Original Image</Typography>
                                <img src={selectedData.data.originalImage} alt="Original" style={{ width: 200, height: "auto", borderRadius: 10 }} />
                            </Box>
                            <Box textAlign="center">
                                <Typography variant="body1">Processed Image</Typography>
                                <img src={selectedData.data.annotatedImage} alt="Processed" style={{ width: 200, height: "auto", borderRadius: 10 }} />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSelectedData(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ValueImageChart;
